/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { isNumber } from "@adapptio/lib-shared";

function easeInOutSin(time: number): number {
	return (1 + Math.sin(Math.PI * time - Math.PI / 2)) / 2;
}

export interface IAnimateOptions {
	ease?: (time: number) => number;
	duration?: number;
}

export function animate<El extends HTMLElement = HTMLElement, Property extends keyof El = keyof El>(
	property: Property,
	element: El,
	to: number,
	options: IAnimateOptions = {}
): () => void {
	const { ease = easeInOutSin, duration = 300 } = options;

	let start = null;
	const propertyValue = element[property];
	const from = isNumber(propertyValue) ? propertyValue : parseInt(String(propertyValue));
	let cancelled = false;

	const cancel = () => {
		cancelled = true;
	};

	const step = (timestamp) => {
		if (cancelled) {
			return;
		}

		if (start === null) {
			start = timestamp;
		}
		const time = Math.min(1, (timestamp - start) / duration);

		element[property] = (ease(time) * (to - from) + from) as unknown as El[Property];

		if (time >= 1) {
			return;
		}

		requestAnimationFrame(step);
	};

	if (from === to) {
		return cancel;
	}

	requestAnimationFrame(step);
	return cancel;
}
